import { ArrowDownIcon, ArrowUpIcon, CloseIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Text,
    VStack,
} from '@chakra-ui/react';
import { translate } from '../../../i18n';
import {
    MEMBERSHIP_DESCRIPTION_MAX_BULLETS_PER_SECTION,
    MEMBERSHIP_DESCRIPTION_MAX_BULLET_LENGTH,
    MEMBERSHIP_DESCRIPTION_MAX_HEADER_LENGTH,
    MEMBERSHIP_DESCRIPTION_MAX_SECTIONS,
    MEMBERSHIP_DESCRIPTION_PREVIEW_BULLETS,
    MembershipDescriptionSection,
} from '../../../models/user-membership/dto/user-membership.dto';

/** Moves `index` by `offset`, returning the same array when that would fall off either end. */
function movedBy<T>(items: T[], index: number, offset: number): T[] {
    const target = index + offset;
    if (target < 0 || target >= items.length) return items;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
}

type Props = {
    value?: MembershipDescriptionSection[] | null;
    onChange: (next: MembershipDescriptionSection[] | null) => void;
};

export default function DescriptionSectionsEditor({ value, onChange }: Props) {
    const sections = value ?? [];
    const isOverrideActive = sections.length > 0;

    const enableOverride = () => {
        onChange([{ header: null, bullets: [''] }]);
    };

    const clearOverride = () => {
        onChange(null);
    };

    const updateSection = (index: number, patch: Partial<MembershipDescriptionSection>) => {
        const next = sections.map((section, i) => (i === index ? { ...section, ...patch } : section));
        onChange(next);
    };

    const addSection = () => {
        if (sections.length >= MEMBERSHIP_DESCRIPTION_MAX_SECTIONS) return;
        onChange([...sections, { header: null, bullets: [''] }]);
    };

    const removeSection = (index: number) => {
        const next = sections.filter((_, i) => i !== index);
        // Clearing the last section collapses the editor back to auto-generated mode.
        onChange(next.length === 0 ? null : next);
    };

    const addBullet = (sectionIndex: number) => {
        const section = sections[sectionIndex];
        if (section.bullets.length >= MEMBERSHIP_DESCRIPTION_MAX_BULLETS_PER_SECTION) return;
        updateSection(sectionIndex, { bullets: [...section.bullets, ''] });
    };

    const removeBullet = (sectionIndex: number, bulletIndex: number) => {
        const section = sections[sectionIndex];
        // Always keep at least one bullet row per section — removing it removes the whole section.
        if (section.bullets.length <= 1) {
            removeSection(sectionIndex);
            return;
        }
        updateSection(sectionIndex, {
            bullets: section.bullets.filter((_, i) => i !== bulletIndex),
        });
    };

    const updateBullet = (sectionIndex: number, bulletIndex: number, text: string) => {
        const section = sections[sectionIndex];
        const bullets = section.bullets.map((b, i) => (i === bulletIndex ? text : b));
        updateSection(sectionIndex, { bullets });
    };

    // MYS-27 — order IS the display priority. The player's card shows the first
    // MEMBERSHIP_DESCRIPTION_PREVIEW_BULLETS points and hides the rest behind a
    // "+N" pill, walking sections in order and then bullets in order, so these
    // two controls are the whole answer to "how do I choose what shows first?".
    const moveSection = (sectionIndex: number, offset: number) => {
        onChange(movedBy(sections, sectionIndex, offset));
    };

    const moveBullet = (sectionIndex: number, bulletIndex: number, offset: number) => {
        const section = sections[sectionIndex];
        updateSection(sectionIndex, {
            bullets: movedBy(section.bullets, bulletIndex, offset),
        });
    };

    // Mirrors mobile's splitSectionsForPreview: blanks are dropped before the
    // cut-off is applied (the API strips them on save), so an unfilled row must
    // not consume one of the preview slots here either.
    const previewBulletKeys = new Set<string>();
    let previewBudget = MEMBERSHIP_DESCRIPTION_PREVIEW_BULLETS;
    let filledBulletCount = 0;
    sections.forEach((section, sectionIndex) => {
        section.bullets.forEach((bullet, bulletIndex) => {
            if (!bullet.trim()) return;
            filledBulletCount += 1;
            if (previewBudget > 0) {
                previewBulletKeys.add(`${sectionIndex}:${bulletIndex}`);
                previewBudget -= 1;
            }
        });
    });
    const hiddenBulletCount = Math.max(
        0,
        filledBulletCount - MEMBERSHIP_DESCRIPTION_PREVIEW_BULLETS,
    );

    return (
        <FormControl>
            <FormLabel>{translate('userMembership.description_sections_label')}</FormLabel>
            <FormHelperText mt={0} mb={3}>
                {translate('userMembership.description_sections_helper')}
            </FormHelperText>

            {!isOverrideActive && (
                <Button size="sm" variant="outline" onClick={enableOverride}>
                    {translate('userMembership.description_sections_enable')}
                </Button>
            )}

            {isOverrideActive && (
                <VStack align="stretch" spacing={4}>
                    {/* MYS-27 — answers "how do I choose what shows in priority?"
                        directly in the editor: there is no separate priority
                        field, order is the priority, and the badge marks the
                        exact points the player sees before tapping "+". */}
                    <Box
                        borderWidth="1px"
                        borderRadius="md"
                        borderStyle="dashed"
                        px={3}
                        py={2}
                    >
                        {/* An override with nothing filled in is not an
                            override: the API stores null and the app falls
                            back to the auto-generated description. Saying
                            "the first 4 points are shown" here would be a
                            straight lie about what the player sees. */}
                        <Text fontSize="xs" color="gray.500">
                            {filledBulletCount === 0
                                ? translate('userMembership.description_sections_empty_hint')
                                : translate('userMembership.description_sections_priority_hint').replace(
                                      '{count}',
                                      String(MEMBERSHIP_DESCRIPTION_PREVIEW_BULLETS),
                                  )}
                        </Text>
                        {hiddenBulletCount > 0 && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                {translate('userMembership.description_sections_hidden_hint').replace(
                                    '{count}',
                                    String(hiddenBulletCount),
                                )}
                            </Text>
                        )}
                    </Box>

                    {sections.map((section, sectionIndex) => (
                        <Box
                            key={sectionIndex}
                            borderWidth="1px"
                            borderRadius="md"
                            p={3}
                        >
                            <HStack mb={2} justify="space-between" align="center">
                                <Text fontSize="sm" fontWeight="semibold" color="gray.500">
                                    {translate('userMembership.description_section_title').replace(
                                        '{index}',
                                        String(sectionIndex + 1),
                                    )}
                                </Text>
                                <HStack spacing={0}>
                                    <IconButton
                                        aria-label={translate('userMembership.description_section_move_up')}
                                        size="xs"
                                        variant="ghost"
                                        icon={<ArrowUpIcon />}
                                        isDisabled={sectionIndex === 0}
                                        onClick={() => moveSection(sectionIndex, -1)}
                                    />
                                    <IconButton
                                        aria-label={translate('userMembership.description_section_move_down')}
                                        size="xs"
                                        variant="ghost"
                                        icon={<ArrowDownIcon />}
                                        isDisabled={sectionIndex === sections.length - 1}
                                        onClick={() => moveSection(sectionIndex, 1)}
                                    />
                                    <IconButton
                                        aria-label={translate('userMembership.description_section_remove')}
                                        size="xs"
                                        variant="ghost"
                                        icon={<CloseIcon boxSize={2.5} />}
                                        onClick={() => removeSection(sectionIndex)}
                                    />
                                </HStack>
                            </HStack>

                            <FormControl mb={3}>
                                <FormLabel fontSize="sm" mb={1}>
                                    {translate('userMembership.description_section_header_label')}
                                </FormLabel>
                                <Input
                                    size="sm"
                                    placeholder={translate('userMembership.description_section_header_placeholder')}
                                    value={section.header ?? ''}
                                    maxLength={MEMBERSHIP_DESCRIPTION_MAX_HEADER_LENGTH}
                                    onChange={(e) =>
                                        updateSection(sectionIndex, { header: e.target.value || null })
                                    }
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm" mb={1}>
                                    {translate('userMembership.description_section_bullets_label')}
                                </FormLabel>
                                <VStack align="stretch" spacing={2}>
                                    {section.bullets.map((bullet, bulletIndex) => (
                                        <HStack key={bulletIndex}>
                                            <Input
                                                size="sm"
                                                placeholder={translate('userMembership.description_section_bullet_placeholder')}
                                                value={bullet}
                                                maxLength={MEMBERSHIP_DESCRIPTION_MAX_BULLET_LENGTH}
                                                onChange={(e) =>
                                                    updateBullet(sectionIndex, bulletIndex, e.target.value)
                                                }
                                            />
                                            {previewBulletKeys.has(`${sectionIndex}:${bulletIndex}`) && (
                                                <Badge
                                                    colorScheme="blue"
                                                    fontSize="0.65rem"
                                                    flexShrink={0}
                                                >
                                                    {translate('userMembership.description_section_bullet_visible')}
                                                </Badge>
                                            )}
                                            <IconButton
                                                aria-label={translate('userMembership.description_section_bullet_move_up')}
                                                size="xs"
                                                variant="ghost"
                                                icon={<ArrowUpIcon />}
                                                isDisabled={bulletIndex === 0}
                                                onClick={() => moveBullet(sectionIndex, bulletIndex, -1)}
                                            />
                                            <IconButton
                                                aria-label={translate('userMembership.description_section_bullet_move_down')}
                                                size="xs"
                                                variant="ghost"
                                                icon={<ArrowDownIcon />}
                                                isDisabled={bulletIndex === section.bullets.length - 1}
                                                onClick={() => moveBullet(sectionIndex, bulletIndex, 1)}
                                            />
                                            <IconButton
                                                aria-label={translate('userMembership.description_section_bullet_remove')}
                                                size="xs"
                                                variant="ghost"
                                                icon={<CloseIcon boxSize={2.5} />}
                                                onClick={() => removeBullet(sectionIndex, bulletIndex)}
                                            />
                                        </HStack>
                                    ))}
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        alignSelf="flex-start"
                                        onClick={() => addBullet(sectionIndex)}
                                        isDisabled={
                                            section.bullets.length >= MEMBERSHIP_DESCRIPTION_MAX_BULLETS_PER_SECTION
                                        }
                                    >
                                        {translate('userMembership.description_section_bullet_add')}
                                    </Button>
                                </VStack>
                            </FormControl>
                        </Box>
                    ))}

                    <HStack>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={addSection}
                            isDisabled={sections.length >= MEMBERSHIP_DESCRIPTION_MAX_SECTIONS}
                        >
                            {translate('userMembership.description_section_add')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={clearOverride}>
                            {translate('userMembership.description_sections_reset')}
                        </Button>
                    </HStack>
                    <Divider />
                </VStack>
            )}
        </FormControl>
    );
}
